import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogCTAButtons from "@/components/BlogCTAButtons";
import CanonicalTag from "@/components/CanonicalTag";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import heroImage from "@/assets/investing-in-adus-property-value.png";

const BlogADUPropertyValue = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "Why Investing in ADUs Amplifies Property Value",
    description:
      "Learn why ADUs increase property value through added equity, rental income, and stronger long-term appeal.",
    datePublished: "2026-04-13",
    author: { "@type": "Organization", name: "UDA ADU" },
    publisher: { "@type": "Organization", name: "UDA ADU" },
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/blogs/investing-in-adus-property-value" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navigation />
      <div className="w-full max-w-5xl mx-auto px-4 pt-8">
        <img src={heroImage} alt="Why investing in ADUs amplifies property value" className="w-full rounded-2xl" />
      </div>
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blogs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog
        </Link>
        <p className="text-sm text-muted-foreground mb-2">April 13, 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">Why Investing in ADUs Amplifies Property Value</h1>
        <article className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>Most homeowners first look at an ADU as extra space.</p>
          <p>But the bigger picture is value.</p>
          <p>A well-designed ADU does more than add square footage. It increases how the property performs financially, how buyers see it, and how flexible it becomes over time.</p>
          <p>That is why investing in an ADU amplifies property value.</p>
          <p>At UDA ADU, we see three reasons this happens consistently.</p>
          <p>First, added equity.</p>
          <p>An ADU creates a second livable structure on the same lot. That immediately strengthens the overall utility of the property and makes the home more attractive in a competitive market.</p>
          <p>Second, rental income.</p>
          <p>For many homeowners in California, the ADU is not just an addition. It becomes a long-term income-producing asset. That income potential increases the perceived and practical value of the property.</p>
          <p>Third, long-term appeal.</p>
          <p>Buyers are looking for flexibility. A property with an ADU can serve multigenerational living, guest use, work space, or rental demand. That range of uses makes the property more desirable over time.</p>
          <p>Design still matters.</p>
          <p>The ADUs that increase value the most are the ones that feel intentional. Proper layout, strong curb appeal, and a design that fits the main house all contribute to how the investment is perceived.</p>
          <p>Function matters too.</p>
          <p>A unit that feels easy to live in, easy to access, and easy to maintain will always outperform one that simply checks a box. Livability strengthens value.</p>
          <p>Across California, ADUs continue to stand out because they do more than solve one problem.</p>
          <p>They add space.</p>
          <p>They create income.</p>
          <p>They improve long-term property appeal.</p>
          <p>That is what makes them such a strong investment.</p>
        </article>
        <BlogCTAButtons />
      </main>
      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default BlogADUPropertyValue;
